import React from "react";
import { Link } from "react-router-dom";
import Logo from "@/components/Logo";
import { ShieldCheck, Lock, Fingerprint, Mail } from "lucide-react";

/**
 * Footer — condensed spacing, sharpened typography.
 *
 * Brand hierarchy (do NOT visually pair FetchDay with the tagline):
 *   SpendNova\u2122
 *   Know What's Safe to Spend.        ← tagline belongs to SpendNova
 *   ─────────────────────────────
 *   Operated by FetchDay LLC.          ← separate corporate attribution
 *
 * Mobile/tablet order (≤ 900px):
 *   1. Navigation / policy links (Product + Legal)
 *   2. Contact
 *   3. SpendNova\u2122 (brand block: logo + italic tagline + description + trust badges)
 *   4. Operated by FetchDay LLC. (attribution)
 * (Copyright & disclaimer live in <LegalDisclosure /> below the footer.)
 */
const Footer = ({ navyTheme = false }) => {
  const isDark = navyTheme;
  const surfaceStyle = isDark
    ? {
        background:
          "linear-gradient(160deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.02) 100%)",
        border: "1px solid rgba(255,255,255,0.12)",
        boxShadow: "0 30px 60px -30px rgba(0,0,0,0.55)",
      }
    : undefined;

  const textPrimary = isDark ? "text-white" : "text-[#1E294E]";
  const textMuted = isDark ? "text-[#A8B0BE]" : "text-[#5b6789]";
  const bodyText = isDark ? "text-[#A8B0BE]" : "text-[#33406B]";
  const linkHover = isDark ? "hover:text-[#22C585]" : "hover:text-[#15A76C]";
  const accent = isDark ? "text-[#22C585]" : "text-[#15A76C]";

  const TrustBadge = ({ icon: Icon, label }) => (
    <div
      className={`inline-flex items-center gap-2 px-2.5 py-1 rounded-full text-[11px] font-semibold ${textPrimary}`}
      style={
        isDark
          ? {
              background: "rgba(255,255,255,0.05)",
              border: "1px solid rgba(255,255,255,0.12)",
            }
          : {
              background: "rgba(255,255,255,0.7)",
              border: "1px solid rgba(205,189,253,0.8)",
              color: "#1E294E",
            }
      }
    >
      <Icon size={13} className={accent} />
      <span>{label}</span>
    </div>
  );

  const columnHeading = `text-[10px] uppercase tracking-[0.22em] mb-2.5 font-bold ${textMuted}`;

  return (
    <footer className="relative mt-8 pb-5" data-testid="sn-footer">
      <div
        className="mx-auto w-full max-w-[1200px]"
        style={{
          paddingLeft: "clamp(16px, 4vw, 40px)",
          paddingRight: "clamp(16px, 4vw, 40px)",
          boxSizing: "border-box",
        }}
      >
        <div
          className={`${isDark ? "" : "sn-glass"} rounded-3xl`}
          style={{
            ...surfaceStyle,
            padding: "clamp(18px, 2.6vw, 32px)",
            boxSizing: "border-box",
          }}
        >
          {/*
            Main grid:
            - < lg (900px): single column, order = links → contact → brand
            - ≥ lg: two columns; brand on the LEFT, (links + contact) on the RIGHT
          */}
          <div className="grid gap-7 lg:grid-cols-2 lg:gap-x-12 lg:gap-y-7">

            {/* ---------- LINKS GROUP (Product + Legal) ---------- */}
            <div
              className="order-1 lg:order-none lg:col-start-2 lg:row-start-1"
              data-testid="footer-links-group"
            >
              <div className="flex flex-col sm:flex-row gap-6 sm:gap-10">
                {/* Product */}
                <div className="min-w-0">
                  <div className={columnHeading}>Product</div>
                  <ul className={`space-y-1.5 text-[13px] ${textPrimary}`}>
                    <li>
                      <a href="/#safe-to-spend" className={linkHover}>
                        What's Inside
                      </a>
                    </li>
                    <li>
                      <a href="/#the-promise" className={linkHover}>
                        The Promise
                      </a>
                    </li>
                    <li>
                      <Link to="/install" className={linkHover} data-testid="footer-install-link">
                        Install
                      </Link>
                    </li>
                    <li>
                      <a href="/#waitlist" className={linkHover}>
                        Waitlist
                      </a>
                    </li>
                  </ul>
                </div>

                {/* Legal */}
                <div className="min-w-0">
                  <div className={columnHeading}>Legal</div>
                  <ul className={`space-y-1.5 text-[13px] ${textPrimary}`}>
                    <li>
                      <Link to="/privacy" className={linkHover}>
                        Privacy Policy
                      </Link>
                    </li>
                    <li>
                      <Link to="/terms" className={linkHover}>
                        Terms of Service
                      </Link>
                    </li>
                    <li>
                      <Link to="/support" className={linkHover} data-testid="footer-support-link">
                        Support
                      </Link>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* ---------- CONTACT ---------- */}
            <div
              className="order-2 lg:order-none lg:col-start-2 lg:row-start-2 min-w-0"
              data-testid="footer-contact-block"
            >
              <div className={columnHeading}>Contact</div>
              <ul className={`space-y-1.5 ${textPrimary}`}>
                <li
                  className="flex items-center gap-2 min-w-0"
                  data-testid="footer-contact-row"
                >
                  <Mail
                    size={14}
                    className={`${accent} shrink-0`}
                    aria-hidden="true"
                  />
                  <a
                    href="mailto:support@myspendnova.com"
                    className={`${linkHover} min-w-0`}
                    style={{
                      display: "inline-block",
                      maxWidth: "100%",
                      whiteSpace: "nowrap",
                      wordBreak: "normal",
                      overflowWrap: "normal",
                      fontSize: "clamp(12px, 2vw, 14px)",
                      lineHeight: 1.35,
                    }}
                    data-testid="footer-support-email"
                  >
                    support@myspendnova.com
                  </a>
                </li>
                <li
                  className="flex items-center gap-2 min-w-0"
                  data-testid="footer-privacy-row"
                >
                  <Mail
                    size={14}
                    className={`${accent} shrink-0`}
                    aria-hidden="true"
                  />
                  <a
                    href="mailto:privacy@myspendnova.com"
                    className={`${linkHover} min-w-0`}
                    style={{
                      display: "inline-block",
                      maxWidth: "100%",
                      whiteSpace: "nowrap",
                      wordBreak: "normal",
                      overflowWrap: "normal",
                      fontSize: "clamp(12px, 2vw, 14px)",
                      lineHeight: 1.35,
                    }}
                    data-testid="footer-contact-email"
                  >
                    privacy@myspendnova.com
                  </a>
                </li>
                <li
                  className="flex flex-col gap-0 min-w-0"
                  data-testid="footer-admin-row"
                >
                  <div className={`text-[10px] uppercase tracking-[0.18em] font-bold ${textMuted}`}>
                    For corporate inquiries
                  </div>
                  <div className="flex items-center gap-2 min-w-0">
                    <Mail
                      size={14}
                      className={`${accent} shrink-0`}
                      aria-hidden="true"
                    />
                    <a
                      href="mailto:admin@myspendnova.com"
                      className={`${linkHover} min-w-0`}
                      style={{
                        display: "inline-block",
                        maxWidth: "100%",
                        whiteSpace: "nowrap",
                        wordBreak: "normal",
                        overflowWrap: "normal",
                        fontSize: "clamp(12px, 2vw, 14px)",
                        lineHeight: 1.35,
                      }}
                      data-testid="footer-admin-email"
                    >
                      admin@myspendnova.com
                    </a>
                  </div>
                </li>
              </ul>
            </div>

            {/* ---------- BRAND BLOCK ---------- */}
            <div
              className="order-3 lg:order-none lg:col-start-1 lg:row-start-1 lg:row-span-2 min-w-0"
              data-testid="footer-brand-block"
            >
              {/* Logo with \u2122 */}
              <div
                className="relative inline-block"
                data-testid="sn-footer-logo-wrap"
              >
                <Logo variant="horizontal" size={26} />
                <span
                  aria-hidden="true"
                  className={`absolute font-semibold pointer-events-none select-none ${isDark ? "text-white" : "text-[#0B1D3A]"}`}
                  style={{
                    right: -10,
                    top: -2,
                    fontSize: 10,
                    lineHeight: 1,
                  }}
                >
                  ™
                </span>
              </div>

              {/* Tagline — belongs to SpendNova\u2122 */}
              <p
                className={`mt-3 text-[13px] leading-relaxed ${bodyText}`}
                style={{ fontStyle: "italic" }}
                data-testid="footer-tagline"
              >
                Know What&apos;s Safe to Spend.
              </p>

              {/* Descriptive text */}
              <p className={`mt-1.5 text-[13px] leading-relaxed ${bodyText} max-w-md`}>
                The financial guardrail for modern life &mdash; built on Zero-Trust
                and AES-256 encryption.
              </p>

              {/* Trust badges */}
              <div className="mt-4 flex flex-wrap gap-1.5">
                <TrustBadge icon={Fingerprint} label="Zero-Trust" />
                <TrustBadge icon={Lock} label="AES-256" />
                <TrustBadge icon={ShieldCheck} label="Bunker Security" />
              </div>
            </div>
          </div>

          {/* ---------- ATTRIBUTION (separate line) ---------- */}
          <div
            className={`mt-7 pt-4 text-[11px] leading-relaxed ${textMuted}`}
            data-testid="footer-attribution"
            style={{
              borderTop: isDark
                ? "1px solid rgba(255,255,255,0.08)"
                : "1px solid rgba(205,189,253,0.7)",
            }}
          >
            SpendNova™ is officially owned and operated by FetchDay LLC. All
            rights reserved.
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
